import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuditPermissions } from "../hooks/permissions";
// The area stylesheet travels WITH the component, because this component is
// rendered from OTHER areas' screens. Leaving it on the audit screen's import
// alone would make the link's tone depend on whether the operator had happened
// to open the trail first — module 05's slice-05-A defect, in a file with no
// import statement to make it visible in review.
import "../audit.css";

// THE "HISTORY" AFFORDANCE, as one component any entity detail screen can drop
// in (spec 16 §4).
//
// A global audit list answers "what happened here lately". The question a
// factory actually asks is "who touched THIS drum / product / work order, and
// when?" — and that question is asked FROM the record, not from a search
// screen. So the affordance lives on the record, and it is a deep link into the
// trail pre-filtered to that entity: `/audit?entity_type=…&entity_id=…`.
//
// It renders NOTHING for a principal who cannot read the trail. A link into a
// 403 is worse than no link: it teaches the reader that the product is broken
// rather than that the screen is not theirs. The check is the same fail-closed
// role mirror the office nav uses, and the server refuses independently either
// way (D-040's two layers).
//
// `entityType` is the value the WRITER stamped — `product`, `work_order`,
// `qc_hold`, `waste_events`. It is passed by the calling screen rather than
// derived here, because `audit_log.entity_type` is a free-text column written by
// 117 call sites and no registry maps a screen to its spelling; guessing one
// would produce a link that silently returns an empty trail, which reads as
// "nothing ever happened to this record".

export function AuditTrailLink({
  entityType,
  entityId,
  className,
}: {
  entityType: string;
  entityId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const perms = useAuditPermissions();
  if (!perms.canReadAudit) return null;
  return (
    <Link
      to="/audit"
      search={{ entity_type: entityType, entity_id: entityId } as never}
      className={className ?? "audit-entity-link"}
      data-testid="audit-entity-link"
    >
      {t("audit.entityLink")}
    </Link>
  );
}
